import React, { useState, useRef, useEffect } from "react";
import { Message, Thread } from "../types";
import { MessageBubble } from "./MessageBubble";
import { ChatSettings } from "./ChatSettings";
import { Send, Bot } from "lucide-react";
import { threadService } from "../services/threadService";
import { aiService } from "../services/aiService";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ChatInterfaceProps {
  thread: Thread | null;
  onThreadUpdate: (thread: Thread) => void;
  isConnected: boolean;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  thread,
  onThreadUpdate,
  isConnected,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [timeoutError, setTimeoutError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingThreadId = useRef<string | null>(null);
  const streamingTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      isGenerating &&
      thread &&
      streamingThreadId.current &&
      thread.id !== streamingThreadId.current
    ) {
      console.log(
        "[DEBUG] Thread changed during streaming. Resetting isGenerating."
      );
      setIsGenerating(false);
      streamingThreadId.current = null;
    }
  }, [thread, isGenerating]);

  const clearStreamingTimeout = () => {
    if (streamingTimeout.current) {
      clearTimeout(streamingTimeout.current);
      streamingTimeout.current = null;
    }
  };

  const startStreamingTimeout = () => {
    clearStreamingTimeout();
    streamingTimeout.current = setTimeout(() => {
      console.log("[DEBUG] Streaming timeout reached");
      setTimeoutError("Request timed out. Please try again.");
      setIsGenerating(false);
      streamingThreadId.current = null;
    }, 30000); // 30 second timeout
  };

  useEffect(() => {
    return () => {
      clearStreamingTimeout();
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread?.messages]);



  const handleSendMessage = async () => {
    if (!inputValue.trim() || !isConnected || !thread || isGenerating) return;

    // Clear any previous timeout errors
    setTimeoutError(null);

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      role: "user",
      timestamp: new Date(),
    };

    // Add user message to thread
    const updatedThread = {
      ...thread,
      messages: [...thread.messages, userMessage],
    };
    onThreadUpdate(updatedThread);
    setInputValue("");
    setIsGenerating(true);
    streamingThreadId.current = thread.id;

    try {
      const systemPrompt =
        "You are a helpful AI assistant. Provide accurate and helpful responses.";

      // Add empty assistant message for streaming
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "",
        role: "assistant",
        timestamp: new Date(),
      };

      const threadWithAssistantMessage = {
        ...updatedThread,
        messages: [...updatedThread.messages, assistantMessage],
      };
      onThreadUpdate(threadWithAssistantMessage);

      // Start timeout
      startStreamingTimeout();

      // Convert messages to the format expected by the AI SDK
      const messages = updatedThread.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Stream the response using the AI SDK
      const stream = await aiService.streamResponse(
        thread.settings.selectedModel,
        messages,
        {
          temperature: thread.settings.temperature,
          system: systemPrompt,
        }
      );

      // Handle the stream
      for await (const chunk of stream.textStream) {
        // Get the current thread state from the service
        const currentThread = threadService.getThread(thread.id);
        if (!currentThread) return;

        const updatedMessages = [...currentThread.messages];
        const lastMessage = updatedMessages[updatedMessages.length - 1];

        if (lastMessage && lastMessage.role === "assistant") {
          lastMessage.content += chunk;

          const updatedThread = {
            ...currentThread,
            messages: updatedMessages,
          };
          onThreadUpdate(updatedThread);
        }

        // Reset the timeout on every chunk
        startStreamingTimeout();
      }

      console.log("[DEBUG] Streaming completed");
      clearStreamingTimeout();
      setIsGenerating(false);
      streamingThreadId.current = null;
    } catch (error) {
      console.error("Chat error:", error);
      clearStreamingTimeout();

      // Remove the empty assistant message and add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content:
          "Error: Failed to connect to Ollama. Please check if Ollama is running.",
        role: "assistant",
        timestamp: new Date(),
      };

      // Get current thread and replace the last message with error
      const currentThread = threadService.getThread(thread.id);
      if (currentThread) {
        const messages = [...currentThread.messages];
        if (
          messages.length > 0 &&
          messages[messages.length - 1].role === "assistant"
        ) {
          messages[messages.length - 1] = errorMessage;
        } else {
          messages.push(errorMessage);
        }

        const finalThread = {
          ...currentThread,
          messages,
        };
        onThreadUpdate(finalThread);
      }

      setIsGenerating(false);
      streamingThreadId.current = null;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    if (thread) {
      const clearedThread = { ...thread, messages: [] };
      onThreadUpdate(clearedThread);
    }
  };

  if (!isConnected) {
    return (
      <div className='flex-1 flex items-center justify-center h-full'>
        <Card className='w-full max-w-md'>
          <CardContent className='text-center p-6'>
            <Bot className='h-16 w-16 text-muted-foreground mx-auto mb-4' />
            <h3 className='text-lg font-medium mb-2'>Not Connected</h3>
            <p className='text-muted-foreground mb-4'>
              Please make sure Ollama is running on your system.
            </p>
            <p className='text-sm text-muted-foreground'>
              You can start Ollama by running:{" "}
              <code className='bg-muted px-2 py-1 rounded-sm'>
                ollama serve
              </code>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className='flex-1 flex items-center justify-center h-full'>
        <Card className='w-full max-w-md'>
          <CardContent className='text-center p-6'>
            <Bot className='h-16 w-16 text-muted-foreground mx-auto mb-4' />
            <h3 className='text-lg font-medium mb-2'>No Thread Selected</h3>
            <p className='text-muted-foreground'>
              Please select a thread from the sidebar or create a new one.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='border-b p-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-2'>
            <h2 className='text-lg font-semibold'>{thread.name}</h2>
            <Badge variant='secondary'>{thread.chatMode}</Badge>
          </div>

          <div className='flex items-center gap-2'>
            <ChatSettings
              streaming={thread.settings.streaming}
              onStreamingChange={(streaming) => {
                const updatedThread = {
                  ...thread,
                  settings: { ...thread.settings, streaming },
                };
                onThreadUpdate(updatedThread);
              }}
              temperature={thread.settings.temperature}
              onTemperatureChange={(temperature) => {
                const updatedThread = {
                  ...thread,
                  settings: { ...thread.settings, temperature },
                };
                onThreadUpdate(updatedThread);
              }}
            />
            <Button
              onClick={clearChat}
              variant='ghost'
              size='sm'
              title='Clear chat'
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className='flex-1 px-6 py-4 h-full overflow-y-auto'>
        <div className='space-y-4'>
          {/* Show timeout error if present */}
          {timeoutError && (
            <div className='text-center'>
              <p className='text-red-500 text-sm'>{timeoutError}</p>
            </div>
          )}

          {thread.messages.length === 0 ? (
            <div className='flex items-center justify-center h-full'>
              <div className='text-center'>
                <Bot className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
                <h3 className='text-lg font-medium mb-2'>
                  Start a conversation
                </h3>
                <p className='text-muted-foreground'>Ask me anything!</p>
              </div>
            </div>
          ) : (
            // Render all messages, and if the last is an assistant with empty content and isGenerating, show typing inside it
            thread.messages.map((message, idx) => {
              const isLast = idx === thread.messages.length - 1;
              const showTyping =
                isLast &&
                message.role === "assistant" &&
                message.content === "" &&
                isGenerating;
              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  showTyping={showTyping}
                />
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className='border-t p-4'>
        <div className='flex gap-4 items-center'>
          <div className='flex-1'>
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder='Ask me anything...'
              className='resize-y'
              rows={1}
              disabled={!isConnected || isGenerating}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || !isConnected || isGenerating}
            size='icon'
            title='Send message'
          >
            <Send className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
};
